import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';

import { catchError, NEVER, Observable, switchMap, throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { MpsOauth2Provider } from '../models/mps-provider.model';
import { mpsEndpoints } from '../mps-endpoints';
import { mpsRouteNames } from '../mps-route-names';
import { MpsOauth2Service } from '../services/mps-oauth2.service';
import { MpsUserService } from '../services/mps-user.service';

/** Clé de traduction affichée sur la page de login quand la session a expiré. */
export const SESSION_EXPIRED_REASON = 'messages.api_response.auth.error.session_expired';

@Injectable()
export class MpsUnauthenticatedInterceptor implements HttpInterceptor {
  private oauth2Service = inject(MpsOauth2Service);

  /**
   * MpsUserService est résolu à la demande et non injecté en champ : il dépend
   * de MpsApiService, donc de HttpClient, qui est justement en train de
   * construire cette chaîne d'intercepteurs. L'injecter directement crée une
   * dépendance circulaire au démarrage.
   */
  private injector = inject(Injector);

  /**
   * Sortie du SPA vers le fournisseur d'identité. Isolée en méthode publique
   * pour que les tests puissent l'observer sans faire naviguer le navigateur —
   * même raison que `MpsLoginComponent.redirect()`.
   */
  redirectTo(url: string): void {
    window.location.href = url;
  }

  intercept(req: HttpRequest<unknown>, handler: HttpHandler): Observable<HttpEvent<unknown>> {
    return handler.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && req.url !== environment.api.host + '/' + mpsEndpoints.user) {
          const provider = localStorage.getItem('provider');

          if (provider) {
            return this.refreshToken(provider, req, handler);
          }

          return this.endExpiredSession();
        }

        return throwError(() => error);
      })
    );
  }

  private refreshToken(provider: string, req: HttpRequest<unknown>, handler: HttpHandler) {
    return this.oauth2Service.refreshToken(provider).pipe(
      switchMap((response) => {
        localStorage.setItem('token', response.token);

        // Retry the request
        return this.retryRequest(req, response, handler);
      }),
      catchError(() => this.endExpiredSession())
    );
  }

  private retryRequest(
    req: HttpRequest<unknown>,
    response: { token: string },
    handler: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const clonedRequest = req.clone({
      setHeaders: {
        Authorization: `Bearer ${response.token}`,
      },
    });

    return handler.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.endExpiredSession();
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Met fin à une session que le refresh token ne peut plus sauver, et renvoie
   * l'utilisateur vers l'authentification.
   *
   * Retour de recette (Stella) : un navigateur laissé ouvert plusieurs jours
   * puis une soumission affichait « Une erreur est survenue, veuillez réessayer
   * plus tard », sans reconnexion. Trois raisons, corrigées ici :
   *
   *  1. l'erreur d'origine était propagée en plus de la redirection, donc le
   *     `catchError` des services métier affichait son message générique — que
   *     le recruteur ne peut pas relier à sa session. On renvoie `NEVER` : la
   *     session est morte, on quitte la page, l'appelant n'a rien à traiter ;
   *  2. le token mort restait en localStorage, donc chaque requête suivante
   *     rejouait le même 401 ;
   *  3. `getProviders()` n'avait pas de branche d'erreur : si la liste des
   *     fournisseurs ne répondait pas, l'utilisateur restait sur place sans
   *     aucun message.
   *
   * Pas d'appel de déconnexion côté API : le token est déjà expiré, et on ne
   * déconnecte pas d'EntraID depuis ici. Le nettoyage est donc purement local.
   */
  private endExpiredSession(): Observable<never> {
    this.injector.get(MpsUserService).clearLocalSession();

    this.oauth2Service.getProviders().subscribe({
      next: (providers: MpsOauth2Provider[]) => {
        // Fournisseur unique : on repart directement sur EntraID, qui
        // re-authentifie sans saisie si la session SSO est encore vivante.
        if (providers.length === 1) {
          localStorage.setItem('provider', providers[0].code);
          this.redirectTo(providers[0].url);
          return;
        }

        this.goToLogin();
      },
      error: () => this.goToLogin(),
    });

    return NEVER;
  }

  /**
   * Rechargement complet et non `router.navigateByUrl`, pour deux raisons :
   *
   *  - `MpsUserService.isLoggedIn()` se fonde sur le user gardé en mémoire et
   *    non sur le token. Sans rechargement, `MpsLoginComponent` nous renverrait
   *    aussitôt vers l'accueil, toujours sans session valide ;
   *  - le formulaire recruteur est protégé par `UnsavedChangesGuard`, qui ouvre
   *    un `window.confirm()` dès qu'il est modifié — le cas d'un recruteur en
   *    pleine saisie. Une navigation Angular pouvait donc être annulée et
   *    laisser l'utilisateur bloqué sur un formulaire qu'il ne peut plus
   *    soumettre.
   *
   * La page de login sait déjà afficher une clé de traduction passée en
   * `?error=` (cf. `MpsLoginComponent.ngOnInit`) : on réutilise ce canal pour
   * dire pourquoi l'utilisateur se retrouve là.
   */
  private goToLogin(): void {
    this.redirectTo(`/${mpsRouteNames.auth}/${mpsRouteNames.login}?error=${SESSION_EXPIRED_REASON}`);
  }
}
